import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';

// "Ask your admin" for a credit-spending feature on one project. Shown to
// team members / viewers when they click a feature only admins may run
// (hiring manager auto-discovery). Checks whether they are already approved
// for this project (then hands straight over), shows a pending request, or
// lets them pick an admin and send one. The admin approves from an email
// link; the requester is emailed a link back to the project.

interface Admin { id: number; name: string; email: string }
interface Approval {
  id: number; status: 'pending' | 'approved' | 'declined' | 'expired';
  adminName: string; adminEmail: string; requestedAt: string; decidedAt: string | null;
  alreadyPending?: boolean; alreadyApproved?: boolean;
}

interface Props {
  feature: 'discovery';
  projectId: number;
  onAllowed: () => void;     // approved (or admin) — open the feature
  onClose: () => void;
}

const fmt = (s: string) => new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

const FeatureApprovalPrompt: React.FC<Props> = ({ feature, projectId, onAllowed, onClose }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState('this feature');
  const [approval, setApproval] = useState<Approval | null>(null);
  const [admins, setAdmins] = useState<Admin[]>([]);
  const [adminId, setAdminId] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`/api/approvals/status?feature=${feature}&projectId=${projectId}`, { headers });
        const data = await res.json();
        if (data.allowed) { onAllowed(); return; }
        setLabel(data.label || 'this feature');
        setApproval(data.approval || null);
        const a = await fetch('/api/approvals/admins', { headers });
        const list: Admin[] = (await a.json()).admins || [];
        setAdmins(list);
        if (list.length === 1) setAdminId(list[0].id);
      } catch (e) {
        setError('Could not check your access. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature, projectId]);

  const send = async () => {
    if (!adminId) return;
    setSending(true); setError(null);
    try {
      const res = await fetch('/api/approvals', { method: 'POST', headers, body: JSON.stringify({ feature, projectId, adminUserId: adminId, note }) });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Could not send the request'); return; }
      if (data.approval?.status === 'approved') { onAllowed(); return; }
      setApproval(data.approval);
    } catch (e) {
      setError('Could not send the request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const pending = approval && approval.status === 'pending';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Ask your admin</h3>
            <p className="text-sm text-gray-600 mt-1">
              Only a team admin can run <span className="font-medium text-gray-800">{label}</span>, because it uses your team's credits.
              Send a request and, once approved, you can run it on this project yourself.
            </p>
          </div>
          <button className="text-gray-400 hover:text-gray-700 text-xl leading-none" onClick={onClose} aria-label="Close">×</button>
        </div>

        {loading ? (
          <p className="mt-5 text-sm text-gray-500">Checking…</p>
        ) : pending ? (
          <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <p className="font-medium">Waiting for {approval!.adminName}</p>
            <p className="mt-1 text-amber-800">
              Requested {fmt(approval!.requestedAt)}. They have an email with an Approve button. You will get an email with a link back here as soon as they click it.
            </p>
          </div>
        ) : (
          <div className="mt-5 space-y-3">
            {approval && approval.status === 'declined' && (
              <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                {approval.adminName} declined your last request{approval.decidedAt ? ` on ${fmt(approval.decidedAt)}` : ''}. You can ask again, or ask someone else.
              </p>
            )}
            {admins.length === 0 ? (
              <p className="text-sm text-gray-600">Your team has no other admin to ask. Speak to whoever manages your MarketMapr account.</p>
            ) : (
              <>
                <label className="block text-xs text-gray-600">
                  <span className="block mb-1">Which admin should approve it?</span>
                  <select
                    value={adminId ?? ''}
                    onChange={e => setAdminId(e.target.value ? parseInt(e.target.value, 10) : null)}
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-900 bg-white"
                  >
                    <option value="">Choose…</option>
                    {admins.map(a => <option key={a.id} value={a.id}>{a.name} ({a.email})</option>)}
                  </select>
                </label>
                <label className="block text-xs text-gray-600">
                  <span className="block mb-1">A note for them (optional)</span>
                  <textarea
                    value={note}
                    onChange={e => setNote(e.target.value.slice(0, 500))}
                    rows={2}
                    placeholder="e.g. Need hiring managers for the Nottingham C# search before Friday"
                    className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm text-gray-900"
                  />
                </label>
              </>
            )}
            {error && <p className="text-xs text-red-700">{error}</p>}
            <div className="flex justify-end gap-2 pt-1">
              <button className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900" onClick={onClose}>Cancel</button>
              {admins.length > 0 && (
                <button
                  disabled={!adminId || sending}
                  onClick={send}
                  className="px-4 py-1.5 rounded-md text-sm font-medium bg-violet-600 text-white hover:bg-violet-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {sending ? 'Sending…' : 'Send request'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeatureApprovalPrompt;
